import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api/client';
import {
  Modal, FormRow, TextInput, RiskLevelSelect, Select,
  FormError, SubmitBar,
} from './admin-ui';
import { EntityCreationDrawer, type EntityKind } from './entity-creation-drawer';

/**
 * Slice 7.6 — composition create/edit modal.
 *
 * The headline UX of the threat-catalog admin: pick an asset_type, pick a
 * threat_event from a filtered dropdown, pick a vulnerability, etc., bind
 * one or more sources, save.
 *
 * Editing is the same modal with `existing` populated; asset_type is locked
 * (it's identity-defining for the composition, per the backend repo).
 */

interface AssetType { id: string; code: string; name: string; family: string }
interface DropdownEntity { id: string; legacy_code: string | null; name: string }
interface FilterPayload {
  threat_events: (DropdownEntity & { type: string })[];
  vulnerabilities: (DropdownEntity & { severity: string })[];
  predisposing_conditions: (DropdownEntity & { pervasiveness: string })[];
  impacts: (DropdownEntity & { type: string })[];
  adversarial_sources: (DropdownEntity & { threat_level: string })[];
  non_adversarial_sources: (DropdownEntity & { threat_level: string })[];
}

export interface CompositionFormState {
  asset_type_id: string;
  threat_event_id: string;
  vulnerability_id: string;
  predisposing_condition_id: string;
  impact_id: string;
  impact_level: string;
  legacy_code: string;
  // Sources are tracked as a list of {id, type} pairs
  source_refs: { source_id: string; source_type: 'adversarial' | 'non_adversarial' }[];
}

const EMPTY_FORM: CompositionFormState = {
  asset_type_id: '', threat_event_id: '', vulnerability_id: '',
  predisposing_condition_id: '', impact_id: '', impact_level: '',
  legacy_code: '', source_refs: [],
};

export function CompositionEditor({ open, onClose, existing }: {
  open: boolean;
  onClose: () => void;
  existing?: {
    id: string;
    asset_type_id: string;
    asset_type_name: string;
    threat_event_id: string;
    vulnerability_id: string;
    predisposing_condition_id: string;
    impact_id: string;
    impact_level: string;
    legacy_code: string | null;
    sources?: { source_id: string; source_type: 'adversarial' | 'non_adversarial' }[];
  };
}) {
  const isEdit = !!existing;
  const qc = useQueryClient();
  const [form, setForm] = useState<CompositionFormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Slice 7.9 (item 3): drawer state — which entity kind, if any, is being
  // created inline. null = closed.
  const [drawerKind, setDrawerKind] = useState<EntityKind | null>(null);

  // Reset form when modal opens
  useEffect(() => {
    if (!open) return;
    if (existing) {
      setForm({
        asset_type_id: existing.asset_type_id,
        threat_event_id: existing.threat_event_id,
        vulnerability_id: existing.vulnerability_id,
        predisposing_condition_id: existing.predisposing_condition_id,
        impact_id: existing.impact_id,
        impact_level: existing.impact_level,
        legacy_code: existing.legacy_code ?? '',
        source_refs: existing.sources ?? [],
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setError(null);
  }, [open, existing]);

  // Asset-type dropdown
  const assetTypesQ = useQuery({
    queryKey: ['admin', 'asset-types'],
    queryFn: () => api.get<{ items: AssetType[] }>('/admin/threat-catalog/asset-types'),
    enabled: open,
  });

  // Filter payload (entity dropdowns) — re-fetched when asset_type_id changes
  const filterQ = useQuery({
    queryKey: ['admin', 'filter-by-asset-type', form.asset_type_id],
    queryFn: () => api.get<FilterPayload>(`/admin/threat-catalog/filter-by-asset-type/${form.asset_type_id}`),
    enabled: open && !!form.asset_type_id,
  });

  function patch<K extends keyof CompositionFormState>(key: K, val: CompositionFormState[K]) {
    setForm((f) => ({ ...f, [key]: val }));
  }

  function toggleSource(source_id: string, source_type: 'adversarial' | 'non_adversarial') {
    setForm((f) => {
      const exists = f.source_refs.find(
        (s) => s.source_id === source_id && s.source_type === source_type,
      );
      if (exists) {
        return {
          ...f,
          source_refs: f.source_refs.filter(
            (s) => !(s.source_id === source_id && s.source_type === source_type),
          ),
        };
      }
      return {
        ...f,
        source_refs: [...f.source_refs, { source_id, source_type }],
      };
    });
  }

  async function handleSubmit() {
    setError(null);

    // Local validation
    if (!form.asset_type_id) return setError('Asset type is required.');
    if (!form.threat_event_id) return setError('Threat event is required.');
    if (!form.vulnerability_id) return setError('Vulnerability is required.');
    if (!form.predisposing_condition_id) return setError('Predisposing condition is required.');
    if (!form.impact_id) return setError('Impact is required.');
    if (!form.impact_level) return setError('Impact level is required.');
    if (form.source_refs.length === 0) return setError('At least one threat source is required.');

    setSubmitting(true);
    try {
      const payload = {
        threat_event_id: form.threat_event_id,
        vulnerability_id: form.vulnerability_id,
        predisposing_condition_id: form.predisposing_condition_id,
        impact_id: form.impact_id,
        impact_level: form.impact_level,
        legacy_code: form.legacy_code.trim() || null,
        source_ids: form.source_refs,
      };

      if (isEdit && existing) {
        await api.put(`/admin/threat-catalog/compositions/${existing.id}`, payload);
      } else {
        await api.post('/admin/threat-catalog/compositions', {
          ...payload,
          asset_type_id: form.asset_type_id,
        });
      }
      // Invalidate browse query so the list refreshes
      qc.invalidateQueries({ queryKey: ['threat-catalog', 'compositions'] });
      onClose();
    } catch (err) {
      const msg = err instanceof ApiError
        ? err.message
        : err instanceof Error ? err.message : String(err);
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit threat composition' : 'New threat composition'}
      widthClass="max-w-3xl w-full"
    >
      <FormError message={error} />

      <FormRow label="Asset type" hint={isEdit ? 'Asset type cannot be changed once set; delete and recreate if needed.' : undefined}>
        {isEdit ? (
          <div className="text-sm text-ink-700 py-1">
            {existing!.asset_type_name}
            <span className="text-ink-400 text-xs ml-2 font-mono">{existing!.asset_type_id.slice(0, 8)}…</span>
          </div>
        ) : (
          <Select
            value={form.asset_type_id}
            onChange={(v) => patch('asset_type_id', v)}
            options={(assetTypesQ.data?.items ?? []).map((t) => ({
              value: t.id,
              label: `${t.family} — ${t.name}`,
            }))}
            required
          />
        )}
      </FormRow>

      <FormRow label="Legacy code" hint="Optional. Customer-internal reference (e.g. 'TC9001').">
        <TextInput
          value={form.legacy_code}
          onChange={(v) => patch('legacy_code', v)}
          placeholder="(optional)"
          maxLength={64}
        />
      </FormRow>

      {form.asset_type_id && (
        <>
          <div className="text-xs text-ink-500 my-3 -mx-1 px-3 py-1.5 bg-ink-50 rounded-sm">
            {filterQ.isLoading
              ? 'Loading available entities…'
              : 'Pick the threat event, vulnerability, predisposing condition, and impact this composition represents.'}
          </div>

          <FormRow label="Threat event">
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <Select
                  value={form.threat_event_id}
                  onChange={(v) => patch('threat_event_id', v)}
                  options={(filterQ.data?.threat_events ?? []).map((e) => ({
                    value: e.id,
                    label: `${e.legacy_code ?? '—'}: ${e.name} (${e.type})`,
                  }))}
                  required
                />
              </div>
              <button
                type="button"
                onClick={() => setDrawerKind('event')}
                className="text-xs text-accent hover:underline whitespace-nowrap"
                title="Create a new threat event in the catalog without leaving this dialog"
              >
                + New event
              </button>
            </div>
          </FormRow>

          <FormRow label="Vulnerability">
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <Select
                  value={form.vulnerability_id}
                  onChange={(v) => patch('vulnerability_id', v)}
                  options={(filterQ.data?.vulnerabilities ?? []).map((e) => ({
                    value: e.id,
                    label: `${e.legacy_code ?? '—'}: ${e.name} [severity=${e.severity}]`,
                  }))}
                  required
                />
              </div>
              <button
                type="button"
                onClick={() => setDrawerKind('vulnerability')}
                className="text-xs text-accent hover:underline whitespace-nowrap"
                title="Create a new vulnerability in the catalog without leaving this dialog"
              >
                + New vulnerability
              </button>
            </div>
          </FormRow>

          <FormRow label="Predispos. condition">
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <Select
                  value={form.predisposing_condition_id}
                  onChange={(v) => patch('predisposing_condition_id', v)}
                  options={(filterQ.data?.predisposing_conditions ?? []).map((e) => ({
                    value: e.id,
                    label: `${e.legacy_code ?? '—'}: ${e.name} [pervasiveness=${e.pervasiveness}]`,
                  }))}
                  required
                />
              </div>
              <button
                type="button"
                onClick={() => setDrawerKind('predisposing_condition')}
                className="text-xs text-accent hover:underline whitespace-nowrap"
                title="Create a new predisposing condition in the catalog without leaving this dialog"
              >
                + New condition
              </button>
            </div>
          </FormRow>

          <FormRow label="Impact">
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <Select
                  value={form.impact_id}
                  onChange={(v) => patch('impact_id', v)}
                  options={(filterQ.data?.impacts ?? []).map((e) => ({
                    value: e.id,
                    label: `${e.legacy_code ?? '—'}: ${e.name} (${e.type})`,
                  }))}
                  required
                />
              </div>
              <button
                type="button"
                onClick={() => setDrawerKind('impact')}
                className="text-xs text-accent hover:underline whitespace-nowrap"
                title="Create a new impact in the catalog without leaving this dialog"
              >
                + New impact
              </button>
            </div>
          </FormRow>

          <FormRow label="Impact level" hint="Severity of the impact dimension for this specific composition.">
            <RiskLevelSelect
              value={form.impact_level}
              onChange={(v) => patch('impact_level', v)}
              required
              extended
            />
          </FormRow>

          <FormRow label="Threat sources" hint="At least one source is required. Select all that apply.">
            <SourceCheckboxList
              filter={filterQ.data}
              selected={form.source_refs}
              onToggle={toggleSource}
            />
          </FormRow>
        </>
      )}

      <SubmitBar
        onCancel={onClose}
        onSubmit={handleSubmit}
        submitting={submitting}
        submitLabel={isEdit ? 'Save changes' : 'Create composition'}
      />
    </Modal>

    {/*
      Slice 7.9 (item 3): inline catalog drawer. Renders as a portal-like
      overlay above the modal. When the user picks "+ New event" (or any
      other entity link), the drawer opens. On Save, the drawer:
        1. POSTs the new entity to the catalog
        2. Calls onCreated with the returned id
        3. We refetch filterQ so the new entity appears in the dropdown
        4. We auto-select it on the form
        5. We close the drawer
      The modal stays mounted and dimmed in the background — block-not-replace
      pattern preserves the user's place in the composition workflow.
    */}
    <EntityCreationDrawer
      open={drawerKind !== null}
      kind={drawerKind ?? 'event'}
      onClose={() => setDrawerKind(null)}
      onCreated={async (newId, _newName) => {
        const kindAtSave = drawerKind;
        // Refetch the filter payload so the newly-created entity appears
        // in the dropdown options.
        await qc.invalidateQueries({ queryKey: ['admin', 'filter-by-asset-type', form.asset_type_id] });
        // Auto-select the new entity in the relevant form field.
        if (kindAtSave === 'event') patch('threat_event_id', newId);
        else if (kindAtSave === 'vulnerability') patch('vulnerability_id', newId);
        else if (kindAtSave === 'predisposing_condition') patch('predisposing_condition_id', newId);
        else if (kindAtSave === 'impact') patch('impact_id', newId);
        // Also invalidate any catalog admin tabs that may be visible
        // elsewhere so the new entity surfaces immediately there too.
        await qc.invalidateQueries({ queryKey: ['admin', 'threat-catalog'] });
        setDrawerKind(null);
      }}
    />
    </>
  );
}

function SourceCheckboxList({ filter, selected, onToggle }: {
  filter: FilterPayload | undefined;
  selected: { source_id: string; source_type: 'adversarial' | 'non_adversarial' }[];
  onToggle: (id: string, type: 'adversarial' | 'non_adversarial') => void;
}) {
  if (!filter) return <div className="text-xs text-ink-400 italic">Loading…</div>;
  const isSelected = (id: string, type: 'adversarial' | 'non_adversarial') =>
    selected.some((s) => s.source_id === id && s.source_type === type);

  return (
    <div className="border border-ink-200 rounded-sm bg-ink-50/30 max-h-56 overflow-y-auto p-2">
      <div className="text-[10px] uppercase tracking-wide text-ink-400 px-1 pt-1 pb-1">
        Adversarial ({filter.adversarial_sources.length})
      </div>
      {filter.adversarial_sources.map((s) => (
        <label key={`adv-${s.id}`} className="flex items-center gap-2 px-1 py-1 hover:bg-white text-xs cursor-pointer">
          <input
            type="checkbox"
            checked={isSelected(s.id, 'adversarial')}
            onChange={() => onToggle(s.id, 'adversarial')}
          />
          <span className="font-mono text-ink-500 w-16">{s.legacy_code ?? '—'}</span>
          <span className="text-ink-700 flex-1">{s.name}</span>
          <span className="text-ink-400 text-[10px]">[{s.threat_level}]</span>
        </label>
      ))}
      <div className="text-[10px] uppercase tracking-wide text-ink-400 px-1 pt-3 pb-1">
        Non-adversarial ({filter.non_adversarial_sources.length})
      </div>
      {filter.non_adversarial_sources.map((s) => (
        <label key={`nadv-${s.id}`} className="flex items-center gap-2 px-1 py-1 hover:bg-white text-xs cursor-pointer">
          <input
            type="checkbox"
            checked={isSelected(s.id, 'non_adversarial')}
            onChange={() => onToggle(s.id, 'non_adversarial')}
          />
          <span className="font-mono text-ink-500 w-16">{s.legacy_code ?? '—'}</span>
          <span className="text-ink-700 flex-1">{s.name}</span>
          <span className="text-ink-400 text-[10px]">[{s.threat_level}]</span>
        </label>
      ))}
    </div>
  );
}
